import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Divider, Form, Input, message, Row, Select } from 'antd';
import moment from 'moment';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IListColumn, ISortColumn, PaginationResponse } from 'src/@types/search-params';
import { getAllTopup, getTopupName } from 'src/api/register_and_cancel_and_load_package';
import CGridView from 'src/components/CGridView';
import TableContent from 'src/components/CTable';
import { usePermission } from 'src/hooks/usePermission';
import useWindowSize from 'src/hooks/useWindowSize';
import { useAppSelector } from 'src/redux/hook';
import { setGlobalState } from 'src/redux/slice/globalSlice';
import { orderStatus } from 'src/shared/common/package_assigned';
import { IOrderStatus, IPackageAssigned } from 'src/shared/interface/package_assigned';
import { exportExcel, formatDate, formatMoney, parseQueryString } from 'src/utils';

function TopupListPage() {
  const size = useWindowSize();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [form] = Form.useForm();
  const [_, setSearchParams] = useSearchParams();
  const merchantInfo: string | null = localStorage.getItem('merchantInfo');
  const { openTabs, activeKeyId } = useAppSelector((state) => state.global);

  const [data, setData] = useState<PaginationResponse<IPackageAssigned[]>>();
  const [sortColumn, setSortColumn] = useState<ISortColumn>();
  const [dataTopupName, setDataTopupName] = useState<IOrderStatus[]>([]);

  const { hasPermission, PERMISSIONS } = usePermission();

  const fetchGetTopupName = async () => {
    await getTopupName().then((res: any) => {
      if (res) {
        const dataPackage: IOrderStatus[] = [{ value: '', label: 'Tất cả' }];
        res?.map((item: any) => {
          dataPackage?.push({
            label: item.name,
            value: item.name,
          });
        });
        setDataTopupName(dataPackage);
      }
    });
  };

  function handleExport() {
    const link = document.createElement('a');
    link.target = '_blank';
    link.download = `danh_sach_nap_tien_${moment().format('DDMMYYYYHHmmss')}.xlsx`;

    exportExcel(
      process.env.REACT_APP_API_URL + '/api/topup-provider/export-topup' + location.search,
    )
      .then((res) => {
        link.href = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
        link.click();
      })
      .catch((error) => {
        if (error.status === 403) {
          message.error(`Tài khoản của bạn không có quyền truy cập vào chức năng này`);
        }
      });
  }

  function handleFormSearch(value: any) {
    let values = { ...value };
    values = {
      ...values,
      search: values.search?.trim(),
      dateFrom:
        value.dateRange && value?.dateRange[0]
          ? value?.dateRange[0].format('YYYY-MM-DD')
          : value.dateFrom ?? undefined,
      dateTo:
        value.dateRange && value?.dateRange[1]
          ? value?.dateRange[1].format('YYYY-MM-DD')
          : value.dateTo ?? undefined,
      dateRange: undefined,
      pageIndex: 1,
      pageSize: 10,
    };
    Object.keys(values).forEach((key) => {
      if (!values[key]) {
        delete values[key];
      }
    });
    setSearchParams(values);
    fetchListPackageAssigned(values);
  }

  function openCreateTopupPage() {
    dispatch(
      setGlobalState({
        activeKeyMenu: 'topup/create',
      }),
    );
    if (!openTabs.find((tab) => tab.key === 'topup/create')) {
      dispatch(
        setGlobalState({
          openTabs: [
            ...openTabs,
            {
              label: 'Tạo mới đơn hàng nạp tiền',
              children: '',
              key: 'topup/create',
              closable: true,
            },
          ],
        }),
      );
    }
    dispatch(setGlobalState({ order: undefined }));
    navigate(`/management/topup/create`);
  }

  const handleDetailTopup = (order: any) => {
    dispatch(
      setGlobalState({
        activeKeyMenu: 'topup/detail' + '?id=' + order.id,
      }),
    );
    if (!openTabs.find((tab) => tab.key === 'topup/detail' + '?id=' + order.id)) {
      dispatch(
        setGlobalState({
          openTabs: [
            ...openTabs,
            {
              label: 'Xem chi tiết Nạp tiền ' + order.id,
              children: '',
              key: 'topup/detail' + '?id=' + order.id,
              closable: true,
            },
          ],
        }),
      );
    }
    navigate(`/management/topup/detail?id=${order.id}`);
  };

  const columns: IListColumn[] = [
    { name: 'STT', fieldName: 'index', width: 20, isHideSort: true },
    {
      name: 'Mã đơn',
      fieldName: 'id',
      renderSpecialCard: (order: IPackageAssigned) => (
        <button
          disabled={!hasPermission(PERMISSIONS.TOPUP.VIEW_DETAIL)}
          className={`underline md:whitespace-nowrap text-right cursor-pointer bg-transparent border-transparent ${
            hasPermission(PERMISSIONS.TOPUP.VIEW_DETAIL) ? 'text-blue-400' : 'text-gray-400'
          } font-semibold`}
          onClick={() => handleDetailTopup(order)}
        >
          {order.id ?? ''}
        </button>
      ),
    },
    {
      name: 'Mệnh giá',
      fieldName: 'topupName',
      renderSpecialCard: (record: IPackageAssigned) => {
        return <span>{record.topupName ? record.topupName + 'đ' : 0}</span>;
      },
    },
    {
      name: 'Trạng thái',
      fieldName: 'statusString',
      renderSpecialCard: (record: IPackageAssigned) => {
        const status = orderStatus.find((item) => item.value === record.statusString);
        return <span className='whitespace-nowrap'>{status?.label ?? ''}</span>;
      },
    },
    {
      name: 'Số giao dịch thanh toán',
      fieldName: 'transactionId',
    },
    {
      name: 'Thành tiền',
      fieldName: 'toTalAmount',
      renderSpecialCard: (record: IPackageAssigned) => {
        return <span>{record.toTalAmount ? formatMoney(record.toTalAmount, 'đ') : 0}</span>;
      },
    },
    {
      name: 'Tổng cộng',
      fieldName: 'countSerial',
      isHideSort: true,
      width: '5%',
    },
    {
      name: 'Thành công',
      fieldName: 'quantitySuccess',
      isHideSort: true,
      width: '5%',
    },
    {
      name: 'Thất bại',
      fieldName: 'quantityFailed',
      isHideSort: true,
      width: '5%',
    },
    {
      name: 'Tổng mệnh giá nạp thành công',
      fieldName: 'quantityPriceSuccess',
      isHideSort: true,
      renderSpecialCard: (record: IPackageAssigned) => {
        return (
          <span>
            {record.quantityPriceSuccess ? formatMoney(record.quantityPriceSuccess, 'đ') : 0}
          </span>
        );
      },
    },
    {
      name: 'Tổng mệnh giá nạp thất bại',
      fieldName: 'quantityPriceFailed',
      isHideSort: true,
      renderSpecialCard: (record: IPackageAssigned) => {
        return (
          <span>
            {record.quantityPriceFailed ? formatMoney(record.quantityPriceFailed, 'đ') : 0}
          </span>
        );
      },
    },
    {
      name: 'Người tạo',
      fieldName: 'createdBy',
    },
    {
      name: 'Ngày tạo',
      fieldName: 'createdAt',
      renderSpecialCard: (record: IPackageAssigned) => {
        return <span>{record.createdAt ? formatDate(record.createdAt) : ''}</span>;
      },
    },
    {
      name: 'Ngày cập nhật cuối',
      fieldName: 'updatedAt',
      renderSpecialCard: (record: IPackageAssigned) => {
        return (
          <span>
            {record.updatedAt &&
            record.createdAt &&
            new Date(record.updatedAt).getTime() === new Date(record.createdAt).getTime()
              ? ''
              : record.updatedAt
              ? formatDate(record.updatedAt)
              : ''}
          </span>
        );
      },
    },
    {
      name: 'Người cập nhật cuối',
      fieldName: 'updatedBy',
    },
    {
      name: 'Thao tác',
      fieldName: 'id',
      renderSpecialCard: (order) => (
        <button
          disabled={!hasPermission(PERMISSIONS.TOPUP.VIEW_DETAIL)}
          className={`underline whitespace-nowrap cursor-pointer bg-transparent border-transparent ${
            hasPermission(PERMISSIONS.TOPUP.VIEW_DETAIL) ? 'text-blue-400' : 'text-gray-400'
          } font-semibold`}
          onClick={() => handleDetailTopup(order)}
        >
          Xem chi tiết
        </button>
      ),
      isHideSort: true,
    },
  ];

  const fetchListPackageAssigned = async (params = {}) => {
    const res = await getAllTopup(params);
    if (res) {
      setData(res);
    }
  };
  useEffect(() => {
    fetchGetTopupName();
    fetchListPackageAssigned();
    form.resetFields();
    setSortColumn(undefined);
  }, [activeKeyId]);

  return (
    <Fragment>
      <Form
        onFinish={handleFormSearch}
        layout={size.width < 768 ? 'vertical' : 'inline'}
        className='gap-2'
      >
        <Form.Item name='search'>
          <Input prefix={<SearchOutlined />} placeholder='Mã đơn' allowClear />
        </Form.Item>
        <Form.Item name='topupName' label='Mệnh giá'>
          <Select
            showSearch
            className='min-w-[150px]'
            placeholder='Vui lòng chọn'
            defaultValue={''}
            options={dataTopupName}
          ></Select>
        </Form.Item>
        <Form.Item name='status' label='Trạng thái'>
          <Select
            showSearch
            className='min-w-[150px]'
            placeholder='Vui lòng chọn'
            defaultValue={''}
            options={orderStatus}
          ></Select>
        </Form.Item>
        <Form.Item name='' label='Người tạo'>
          <Select
            showSearch
            className='min-w-[150px]'
            defaultValue={merchantInfo ? JSON.parse(merchantInfo).merchantName : ''}
          >
            {/* <Select.Option value='merchantName'>Tất cả</Select.Option> */}
            <Select.Option value='merchantName'>
              {merchantInfo ? JSON.parse(merchantInfo).merchantName : ''}
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item name='dateRange' label='Ngày tạo'>
          <DatePicker.RangePicker
            disabledDate={(current) => current && current >= moment().endOf('day')}
            placeholder={['từ', 'đến']}
            format={'DD/MM/YYYY'}
          />
        </Form.Item>

        <div className='row-button' style={{ justifyContent: 'flex-end', margin: '1.5rem 0' }}>
          <Button type='primary' htmlType='submit'>
            Tìm kiếm
          </Button>
          {hasPermission(PERMISSIONS.TOPUP.CREATE) && (
            <Button onClick={openCreateTopupPage}>Tạo mới</Button>
          )}
          {hasPermission(PERMISSIONS.TOPUP.EXPORT) && (
            <Button onClick={handleExport}>Export</Button>
          )}
        </div>
      </Form>

      <div>
        {size.width < 1024 ? (
          <CGridView
            length={data?.results.length}
            totalRecord={data?.rowCount ?? 0}
            columns={columns}
            title={() => <div className='text-xl font-bold my-3'>Danh sách đơn hàng nạp tiền</div>}
          >
            {data?.results && data?.results.length > 0 ? (
              data?.results.map((item: IPackageAssigned) => {
                return (
                  <div key={'data-row-' + item.id}>
                    <Divider className='border-t-black' />

                    <Row className='mb-6 items-center' gutter={[4, 16]}>
                      {columns.map((ele) => {
                        if (ele.fieldName !== 'index' && ele.fieldName !== 'action') {
                          return (
                            <>
                              <Col span={11} className={`font-bold label`}>
                                <div className='flex gap-2 items-center'>{ele.name}</div>
                              </Col>
                              <Col className='break-words text-right' span={13}>
                                {ele?.renderSpecialCard
                                  ? ele?.renderSpecialCard(item, null, null, null)
                                  : item[`${ele?.fieldName}`]}
                              </Col>
                            </>
                          );
                        } else if (ele.fieldName === 'action') {
                          return (
                            <Col key={'column-' + item.id + ele.fieldName} span={24}>
                              <div className='flex gap-6'>
                                {ele?.renderSpecialCard
                                  ? ele?.renderSpecialCard(item, null, null, null)
                                  : item[`${ele?.fieldName}`]}
                              </div>
                            </Col>
                          );
                        } else {
                          return <></>;
                        }
                      })}
                    </Row>
                  </div>
                );
              })
            ) : (
              <p>{'Không có dữ liệu'}</p>
            )}
          </CGridView>
        ) : (
          <TableContent
            bordered
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'Không tìm thấy kết quả' }}
            dataSource={data?.results ?? []}
            columns={columns}
            totalRecord={data?.rowCount ?? 0}
            sortColumn={sortColumn}
            onPageChange={(pageNumber, pageSize) => {
              const pagination = parseQueryString(location.search);
              fetchListPackageAssigned({
                ...pagination,
                pageIndex: pageNumber ?? 1,
                pageSize: pageSize ?? 10,
                sortField: pagination.sortField ?? undefined,
                asc: pagination.asc ?? undefined,
              });
            }}
            onSort={(sort: ISortColumn) => {
              const pagination = parseQueryString(location.search);
              fetchListPackageAssigned({
                ...pagination,
                pageIndex: pagination.pageIndex ?? 1,
                pageSize: pagination.pageSize ?? 10,
                sortField: sort.fieldName,
                asc:
                  sort.orderType === 'asc' ? true : sort.orderType === 'desc' ? 'false' : undefined,
              });
            }}
          />
        )}
      </div>
    </Fragment>
  );
}

export default TopupListPage;
